import React, { useState } from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';

const TypeArticleList = ({
  typeArticles,
  loading,
  pagination,
  onPageChange,
  onPageSizeChange,
  onView,
  onEdit,
  onDelete
}) => {
  const { isDarkMode } = useTheme();
  const [viewMode, setViewMode] = useState('cards');

  const getFamilleColor = (familleId) => {
    if (isDarkMode) return 'bg-gray-700 text-gray-200 border-gray-600';
    const colors = {
      1: 'bg-blue-100 text-blue-800 border-blue-200',
      2: 'bg-green-100 text-green-800 border-green-200',
      3: 'bg-purple-100 text-purple-800 border-purple-200',
    };
    return colors[familleId] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getFamilleIcon = (familleId) => {
    const icons = {
      1: 'fas fa-fire-extinguisher',
      2: 'fas fa-spray-can',
      3: 'fas fa-tools',
    };
    return icons[familleId] || 'fas fa-tag';
  };

  if (loading) {
    return (
      <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-sm border p-8`}>
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <div key={index} className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-xl h-48`}></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (typeArticles.length === 0) {
    return (
      <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-sm border p-12`}>
        <div className="text-center">
          <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-6`}>
            <i className={`fas fa-tags text-4xl ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}></i>
          </div>
          <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-3`}>Aucun type d'article trouvé</h3>
          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-6`}>
            Commencez par créer votre premier type d'article ou modifiez vos critères de recherche.
          </p>
          <button className={`px-6 py-3 ${isDarkMode ? 'bg-gray-600 text-white hover:bg-gray-700' : 'bg-primary-600 text-white hover:bg-primary-700'} rounded-xl transition-all duration-200 font-semibold`}>
            <i className="fas fa-plus mr-2"></i>
            Créer un type d'article
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* View Toggle */}
      <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-sm border p-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              {pagination.total} type(s) d'article • Page {pagination.page} sur {pagination.maxPage}
            </span>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} flex items-center rounded-lg p-1`}>
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  viewMode === 'cards'
                    ? `${isDarkMode ? 'bg-gray-800 text-primary-400 shadow-sm' : 'bg-white text-primary-600 shadow-sm'}`
                    : `${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`
                }`}
              >
                <i className="fas fa-th-large mr-1"></i>
                Cartes
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  viewMode === 'table'
                    ? `${isDarkMode ? 'bg-gray-800 text-primary-400 shadow-sm' : 'bg-white text-primary-600 shadow-sm'}`
                    : `${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`
                }`}
              >
                <i className="fas fa-list mr-1"></i>
                Liste
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-sm border overflow-hidden`}>
        {viewMode === 'cards' ? (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {typeArticles.map((typeArticle) => (
                <div
                  key={typeArticle.id}
                  className={`${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'} rounded-2xl p-6 border hover:shadow-lg transition-all duration-200 hover:scale-105 group`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getFamilleColor(typeArticle.familleId)}`}>
                      <i className={`${getFamilleIcon(typeArticle.familleId)} text-lg`}></i>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => onView(typeArticle)}
                        className={`w-8 h-8 rounded-lg transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100 ${
                          isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                        }`}
                      >
                        <i className="fas fa-eye text-sm"></i>
                      </button>
                      <button
                        onClick={() => onEdit(typeArticle)}
                        className={`w-8 h-8 rounded-lg transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100 ${
                          isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                        }`}
                      >
                        <i className="fas fa-edit text-sm"></i>
                      </button>
                      <button
                        onClick={() => onDelete(typeArticle)}
                        className={`w-8 h-8 rounded-lg transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100 ${
                          isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-red-100 text-red-600 hover:bg-red-200'
                        }`}
                      >
                        <i className="fas fa-trash text-sm"></i>
                      </button>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>{typeArticle.designation}</h3>
                    <div className={`flex items-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <i className="fas fa-hashtag mr-1"></i>
                      ID: {typeArticle.id}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold border ${getFamilleColor(typeArticle.familleId)}`}>
                      Famille {typeArticle.familleId}
                    </div>
                    <button
                      onClick={() => onView(typeArticle)}
                      className={`${isDarkMode ? 'text-primary-400 hover:text-white' : 'text-primary-600 hover:text-primary-700'} font-medium text-sm`}
                    >
                      Voir détails <i className="fas fa-arrow-right ml-1"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <tr>
                  <th className={`px-6 py-4 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Désignation</th>
                  <th className={`px-6 py-4 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Famille</th>
                  <th className={`px-6 py-4 text-left text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>ID</th>
                  <th className={`px-6 py-4 text-right text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>Actions</th>
                </tr>
              </thead>
              <tbody className={isDarkMode ? 'divide-y divide-gray-700' : 'divide-y divide-gray-200'}>
                {typeArticles.map((typeArticle) => (
                  <tr key={typeArticle.id} className={isDarkMode ? 'hover:bg-gray-700 transition-colors' : 'hover:bg-gray-50 transition-colors'}>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${getFamilleColor(typeArticle.familleId)}`}>
                          <i className={`${getFamilleIcon(typeArticle.familleId)}`}></i>
                        </div>
                        <div>
                          <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{typeArticle.designation}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${getFamilleColor(typeArticle.familleId)}`}>
                        Famille {typeArticle.familleId}
                      </div>
                    </td>
                    <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>#{typeArticle.id}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => onView(typeArticle)}
                          className={`w-8 h-8 rounded-lg transition-colors flex items-center justify-center ${
                            isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                          }`}
                        >
                          <i className="fas fa-eye text-sm"></i>
                        </button>
                        <button
                          onClick={() => onEdit(typeArticle)}
                          className={`w-8 h-8 rounded-lg transition-colors flex items-center justify-center ${
                            isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                          }`}
                        >
                          <i className="fas fa-edit text-sm"></i>
                        </button>
                        <button
                          onClick={() => onDelete(typeArticle)}
                          className={`w-8 h-8 rounded-lg transition-colors flex items-center justify-center ${
                            isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-red-100 text-red-600 hover:bg-red-200'
                          }`}
                        >
                          <i className="fas fa-trash text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.maxPage > 1 && (
        <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-sm border p-6`}>
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <select
                value={pagination.pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-300'}`}
              >
                <option value={10}>10 par page</option>
                <option value={25}>25 par page</option>
                <option value={50}>50 par page</option>
              </select>
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Affichage de {((pagination.page - 1) * pagination.pageSize) + 1} à{' '}
                {Math.min(pagination.page * pagination.pageSize, pagination.total)} sur {pagination.total}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => onPageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className={`px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600' : 'border-gray-300'
                }`}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              
              {[...Array(Math.min(5, pagination.maxPage))].map((_, index) => {
                const pageNum = index + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`px-3 py-2 rounded-lg transition-colors ${
                      pagination.page === pageNum
                        ? isDarkMode
                          ? 'bg-primary-700 text-white'
                          : 'bg-primary-600 text-white'
                        : isDarkMode
                        ? 'bg-gray-700 border border-gray-600 text-gray-200 hover:bg-gray-600'
                        : 'border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => onPageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.maxPage}
                className={`px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600' : 'border-gray-300'
                }`}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TypeArticleList;